import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { knessetFromParam, previousKnesset, swornInLabel, useScope } from '../hooks/useScope';
import { formatDayMonth, useSyncPause } from '../hooks/useLastSync';
import KnessetMenu from '../components/KnessetMenu';
import Notice from '../components/Notice';
import { MemberIndexEntry, QueryDoc } from '../types/types';
import { getMemberSummaries, getQueries, getScope } from '../data/cache';
import { photoFor } from '../utils/names';
import { buttonReset, color, font, radius, space, weight } from '../theme';
import PageHeader from '../components/PageHeader';
import SearchField from '../components/SearchField';
import { StatTile, StatTileRow } from '../components/StatTile';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import ListRow from '../components/ListRow';
import Avatar from '../components/Avatar';
import Button from '../components/Button';
import Icon from '../components/Icon';
import { EmptyState, ErrorState } from '../components/States';
import { SkeletonList, SkeletonTiles } from '../components/Skeleton';
import { useBreakpoint } from '../useBreakpoint';
import PageContainer from '../layout/PageContainer';
import TwoPane from '../layout/TwoPane';
import TabBar from '../components/TabBar';
import AgendaMotionList from '../components/agenda/AgendaMotionList';

const PAGE_SIZE = 30;

type QueryStat = 'total' | 'regular' | 'urgent' | 'answered' | 'withReply' | 'pending';

const STAT_FILTERS: Record<QueryStat, (q: QueryDoc) => boolean> = {
  total: () => true,
  regular: q => q.typeDesc === 'רגילה',
  urgent: q => q.typeDesc === 'דחופה',
  answered: q => !!q.replyDate,
  withReply: q => !!q.replyDocUrl || !!q.hasReplyDoc,
  pending: q => !q.replyDate,
};

const STAT_LABELS: { key: QueryStat; label: string }[] = [
  { key: 'total', label: 'שאילתות' },
  { key: 'regular', label: 'רגילות' },
  { key: 'urgent', label: 'דחופות' },
  { key: 'answered', label: 'נענו' },
  { key: 'withReply', label: 'עם מסמך תשובה' },
  { key: 'pending', label: 'ממתינות' },
];

// "משרד הבריאות" → "הבריאות", for compact rows and bars
const trimMinistry = (name: string) =>
  name.replace(/^משרד /, '').replace(/^המשרד ל/, '').replace(/^המשרד ה/, '').replace(/^המשרד /, '');

const formatShortDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('he-IL', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

/* ── Query row ──────────────────────────────────────────────────────────── */

const QueryRow = ({ query: q, mk, first }: { query: QueryDoc; mk?: MemberIndexEntry; first: boolean }) => {
  const answered = !!q.replyDate;
  const who = [mk?.name, q.govMinistryName ? trimMinistry(q.govMinistryName) : null].filter(Boolean).join(' · ');
  const date = formatShortDate(q.submitDate);
  return (
    <ListRow
      divider={!first}
      to={`/queries/${q.id}`}
      state={{ from: 'שאילתות' }}
      leading={mk ? <Avatar name={mk.name} src={photoFor(mk, 36)} size={36} /> : <Avatar name="?" size={36} />}
      title={q.name}
      subtitle={
        <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs, marginTop: space.xxs }}>
          <span style={{ display: 'flex', gap: space.xs, flexWrap: 'wrap' }}>
            <Badge tone={q.typeDesc === 'דחופה' ? 'warning' : 'info'}>{q.typeDesc || 'רגילה'}</Badge>
            <Badge tone={answered ? 'success' : 'neutral'}>{answered ? 'נענתה' : 'ממתינה'}</Badge>
          </span>
          <span>{who}{who && date && ' · '}{date && <span style={{ whiteSpace: 'nowrap' }}>{date}</span>}</span>
        </span>
      }
    />
  );
};

/* ── שאילתות tab ────────────────────────────────────────────────────────── */

// `switcher`: the page's שאילתות / הצעות לסדר היום control, shown under the title
const QueriesTab = ({ switcher }: { switcher: React.ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDesktop } = useBreakpoint();
  const [allQueries, setAllQueries] = useState<QueryDoc[] | null>(null);
  const [members, setMembers] = useState<Record<string, MemberIndexEntry>>({});
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const scope = useScope();
  // Desktop: open by default, it's the filter column's main content
  const [ministryChartOpen, setMinistryChartOpen] = useState(() => isDesktop || (searchParams.get('min') || '') !== '');

  // Filters live in the URL, so they survive Back
  const selectedStat = (searchParams.get('stat') as QueryStat) || 'total';
  const ascending = searchParams.get('dir') === 'asc';
  const search = searchParams.get('q') || '';
  const visibleCount = parseInt(searchParams.get('n') || '', 10) || PAGE_SIZE;
  const ministryParam = searchParams.get('min') || '';
  const ministryFilter = useMemo(() => new Set(ministryParam ? ministryParam.split('|') : []), [ministryParam]);
  // The Knesset shown: the current one, or an archived one (?k=). The data load resolves it
  // from the loaded scope, so a first render's default scope never loads the wrong list.
  const kParam = searchParams.get('k');
  const viewKnesset = knessetFromParam(kParam, scope);
  const viewingCurrent = viewKnesset === scope.knesset;
  const pausedSince = useSyncPause('queries');
  const previous = previousKnesset(scope);
  // Another Knesset is a fresh list: filters and paging reset
  const setKnesset = (k: number) => setSearchParams(() => {
    const next = new URLSearchParams();
    if (k !== scope.knesset) next.set('k', String(k));
    return next;
  }, { replace: true });

  const updateParams = useCallback((changes: Record<string, string | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const keys = Object.keys(changes);
      for (let i = 0; i < keys.length; i++) {
        const v = changes[keys[i]];
        if (v === null || v === '') next.delete(keys[i]); else next.set(keys[i], v);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Tiles: first press filters (newest first), second press flips to oldest first, third resets
  const handleStatClick = (key: QueryStat) => {
    if (selectedStat !== key) updateParams({ stat: key === 'total' ? null : key, dir: null, n: null });
    else if (!ascending) updateParams({ dir: 'asc' });
    else updateParams({ stat: null, dir: null, n: null });
  };
  const toggleMinistry = (name: string) => {
    const next = new Set(ministryFilter);
    if (next.has(name)) next.delete(name); else next.add(name);
    updateParams({ min: Array.from(next).join('|'), n: null });
  };

  // Queries first; the authors' names and photos fill in from the member index
  useEffect(() => {
    let cancelled = false;
    setError(false);
    setAllQueries(null);
    getScope().then(sc => getQueries(knessetFromParam(kParam, sc))).then(list => {
      if (cancelled) return;
      setAllQueries(list);
      getMemberSummaries(list.map(q => q.memberId || '')).then(m => { if (!cancelled) setMembers(m); }).catch(() => { /* ok — names are optional */ });
    }).catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [reloadKey, kParam]);

  // Scroll restoration: save on unmount, restore once the list has loaded
  const restored = useRef(false);
  useEffect(() => () => {
    try { sessionStorage.setItem('queries_scroll', String(window.scrollY)); } catch { /* ok */ }
  }, []);
  useEffect(() => {
    if (restored.current || !allQueries) return;
    restored.current = true;
    try {
      const saved = sessionStorage.getItem('queries_scroll');
      if (saved) { window.scrollTo(0, parseInt(saved, 10) || 0); sessionStorage.removeItem('queries_scroll'); }
    } catch { /* ok */ }
  }, [allQueries]);

  const counts = useMemo(() => {
    const c: Record<QueryStat, number> = { total: 0, regular: 0, urgent: 0, answered: 0, withReply: 0, pending: 0 };
    for (const q of allQueries || []) {
      for (const s of STAT_LABELS) if (STAT_FILTERS[s.key](q)) c[s.key]++;
    }
    return c;
  }, [allQueries]);

  const ministryStats = useMemo(() => {
    const map: Record<string, { total: number; answered: number }> = {};
    for (const q of allQueries || []) {
      const name = q.govMinistryName;
      if (!name) continue;
      if (!map[name]) map[name] = { total: 0, answered: 0 };
      map[name].total++;
      if (q.replyDate) map[name].answered++;
    }
    return Object.keys(map)
      .map(name => ({ name, total: map[name].total, answered: map[name].answered }))
      .sort((a, b) => b.total - a.total);
  }, [allQueries]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const result = (allQueries || []).filter(q =>
      STAT_FILTERS[selectedStat](q) &&
      (ministryFilter.size === 0 || ministryFilter.has(q.govMinistryName || '')) &&
      (!s || (q.name || '').toLowerCase().includes(s))
    );
    // The list is newest first; the second tile press flips it
    return ascending ? result.slice().reverse() : result;
  }, [allQueries, selectedStat, ascending, ministryFilter, search]);

  const visible = filtered.slice(0, visibleCount);
  const hasFilters = selectedStat !== 'total' || ministryFilter.size > 0 || search.trim() !== '';
  const maxTotal = ministryStats.length > 0 ? ministryStats[0].total : 1;

  const searchField = <SearchField value={search} onChange={v => updateParams({ q: v, n: null })} placeholder="חיפוש לפי נושא" />;

  return (
    <>
        <PageHeader
          title="שאילתות"
          subtitle={allQueries ? `${allQueries.length.toLocaleString('he-IL')} שאילתות שהגישו חברי הכנסת ה-${viewKnesset} לשרים` : `שאילתות שהגישו חברי הכנסת ה-${viewKnesset} לשרים`}
          action={<KnessetMenu value={viewKnesset} onChange={setKnesset} />}
        >
          {switcher}
          {isDesktop ? <div style={{ maxWidth: 480 }}>{searchField}</div> : searchField}
        </PageHeader>

        {viewingCurrent && pausedSince && (
          <Notice tone="warning" icon="clock">
            <b>הנתונים נכונים ל-{formatDayMonth(pausedSince)}.</b> הכנסת ה-{scope.knesset + 1} הושבעה, והעדכון יחודש כשהאתר יעבור אליה.
          </Notice>
        )}

        {error && <Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card>}

        {/* A new Knesset with no queries yet: say so and offer the previous Knesset's */}
        {allQueries && allQueries.length === 0 && viewingCurrent && previous !== null && (
          <Card padding={0}>
            <EmptyState
              icon="document"
              title="עוד לא הוגשו שאילתות"
              body={`הכנסת ה-${scope.knesset} הושבעה${swornInLabel(scope) ? ` ב-${swornInLabel(scope)}` : ''}. שאילתות יופיעו כאן כשחברי הכנסת יתחילו להגיש אותן.`}
              action={<Button variant="primary" onClick={() => setKnesset(previous)}>לשאילתות הכנסת ה-{previous}</Button>}
            />
          </Card>
        )}

        {!error && !allQueries && (
          <div data-skeleton="true" style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
            <SkeletonTiles count={3} />
            <SkeletonTiles count={3} />
            <SkeletonList rows={6} avatar />
          </div>
        )}

        {allQueries && !(allQueries.length === 0 && viewingCurrent && previous !== null) && (() => {
          // Desktop: the stat tiles and per-ministry chart form a sticky filter column beside the list
          const filterColumn = (
          <>
            <StatTileRow columns={3}>
              {STAT_LABELS.map(s => (
                <StatTile
                  key={s.key}
                  value={counts[s.key].toLocaleString('he-IL')}
                  label={s.label}
                  selected={selectedStat === s.key}
                  indicator={ascending ? 'asc' : 'desc'}
                  onClick={() => handleStatClick(s.key)}
                  title="לחיצה מסננת; לחיצה נוספת הופכת את סדר התאריכים"
                />
              ))}
            </StatTileRow>

            {/* Queries per ministry: tap a ministry to filter by it */}
            <Card padding={0}>
              <button
                type="button"
                aria-expanded={ministryChartOpen}
                onClick={() => setMinistryChartOpen(o => !o)}
                style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', minHeight: 52, padding: `0 ${space.lg}px`, display: 'flex', alignItems: 'center', gap: space.sm }}
              >
                <span style={{ fontSize: font.heading, fontWeight: weight.bold }}>שאילתות לפי משרד</span>
                {ministryFilter.size > 0 && <Badge tone="info">{ministryFilter.size} נבחרו</Badge>}
                <span style={{ marginInlineStart: 'auto', color: color.textTertiary }}><Icon name={ministryChartOpen ? 'chevronUp' : 'chevronDown'} size={18} /></span>
              </button>
              {ministryChartOpen && (
                <div style={{ padding: `0 ${space.md}px ${space.md}px` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: font.caption, color: color.textSecondary, padding: `0 ${space.xs}px ${space.sm}px` }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: space.xs }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: '#3f7a16' }} />נענו
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: color.track, marginInlineStart: space.sm }} />ממתינות
                    </span>
                    {ministryFilter.size > 0 && (
                      <button type="button" onClick={() => updateParams({ min: null, n: null })} style={{ ...buttonReset, color: color.primary, fontWeight: weight.medium, fontSize: font.small, minHeight: 32 }}>
                        ניקוי הבחירה
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: space.xxs }}>
                    {ministryStats.map(m => {
                      const selected = ministryFilter.has(m.name);
                      const wide = m.total / maxTotal >= 0.5;
                      return (
                        <button
                          key={m.name}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => toggleMinistry(m.name)}
                          style={{
                            ...buttonReset,
                            gridColumn: wide ? 'span 2' : 'span 1',
                            padding: `${space.sm - 2}px ${space.sm}px`,
                            borderRadius: radius.md,
                            background: selected ? color.primarySoft : 'transparent',
                            minHeight: 44,
                          }}
                        >
                          <span style={{ display: 'flex', justifyContent: 'space-between', gap: space.sm, fontSize: font.caption, marginBottom: space.xs }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? color.primary : color.text, fontWeight: selected ? weight.bold : weight.regular }}>{trimMinistry(m.name)}</span>
                            <span style={{ flexShrink: 0, color: color.textSecondary }}>{m.answered}/<b style={{ color: color.text }}>{m.total}</b></span>
                          </span>
                          <span style={{ display: 'block', position: 'relative', height: 6, borderRadius: radius.pill, background: color.surfaceMuted, overflow: 'hidden' }}>
                            <span style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: `${(m.total / maxTotal) * 100}%`, background: selected ? '#B5D4F4' : color.track }} />
                            <span style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: `${(m.answered / maxTotal) * 100}%`, background: selected ? color.primary : '#3f7a16' }} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          </>
          );
          return (
          <TwoPane
            sideWidth={340}
            gap={space.xxl}
            side={isDesktop ? <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>{filterColumn}</div> : filterColumn}
          >

            {hasFilters && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: font.small, color: color.textSecondary }}>
                <span>{filtered.length.toLocaleString('he-IL')} תוצאות</span>
                <button type="button" onClick={() => updateParams({ stat: null, dir: null, min: null, q: null, n: null })} style={{ ...buttonReset, color: color.primary, fontWeight: weight.medium, minHeight: 32 }}>
                  ניקוי הסינון
                </button>
              </div>
            )}

            {filtered.length === 0
              ? (
                <Card padding={0}>
                  <EmptyState
                    title="לא נמצאו שאילתות"
                    action={<Button variant="ghost" onClick={() => updateParams({ stat: null, dir: null, min: null, q: null, n: null })}>הצגת כל השאילתות</Button>}
                  />
                </Card>
              )
              : (
                <Card padding={0}>
                  {visible.map((q, i) => <QueryRow key={q.id} query={q} mk={q.memberId ? members[q.memberId] : undefined} first={i === 0} />)}
                </Card>
              )}

            {visible.length < filtered.length && (
              <Button fullWidth onClick={() => updateParams({ n: String(visibleCount + PAGE_SIZE) })}>
                הצגת עוד ({(filtered.length - visible.length).toLocaleString('he-IL')} נותרו)
              </Button>
            )}
          </TwoPane>
          );
        })()}
    </>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────── */

type PageTab = 'queries' | 'motions';

/* Two tabs (owner's 5d-A, 2026-10-17): שאילתות, and הצעות לסדר היום (?tab=motions,
   components/agenda/AgendaMotionList). A segmented switch under the title; each tab keeps
   its own filters in the URL, and a switch keeps only the Knesset (?k=). */
const QueriesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDesktop } = useBreakpoint();
  const tab: PageTab = searchParams.get('tab') === 'motions' ? 'motions' : 'queries';
  const setTab = (t: PageTab) => setSearchParams(prev => {
    const next = new URLSearchParams();
    const k = prev.get('k');
    if (t === 'motions') next.set('tab', 'motions');
    if (k) next.set('k', k);
    return next;
  }, { replace: true });
  const switcher = (
    <TabBar
      label="תצוגה"
      tabs={[{ key: 'queries', label: 'שאילתות' }, { key: 'motions', label: 'הצעות לסדר היום' }]}
      active={tab}
      onChange={setTab}
      style={isDesktop ? { maxWidth: 420 } : undefined}
    />
  );

  return (
    <Layout width="full">
      <PageContainer>
      <div style={{ padding: isDesktop ? 0 : `${space.sm}px ${space.lg}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.lg }}>
        {tab === 'motions' ? <AgendaMotionList switcher={switcher} /> : <QueriesTab switcher={switcher} />}
      </div>
      </PageContainer>
    </Layout>
  );
};

export default QueriesPage;
